// Landing page copy (T22). Every legal claim matches docs/rules.md and links its gov.uk
// source; content/claims.md maps each claim to its rule. UK English, no scare copy.
import type { ComponentChildren } from 'preact';
import { RotaSketch } from './RotaSketch';

const GOV = {
  restBreaks: 'https://www.gov.uk/rest-breaks-work',
  youngWorkers: 'https://www.gov.uk/rest-breaks-work/young-workers',
  maxHours: 'https://www.gov.uk/maximum-weekly-working-hours',
  nightWork: 'https://www.gov.uk/night-working-hours',
};

export function Hero() {
  return (
    <section aria-labelledby="hero-heading" class="hero">
      <div class="hero-text">
        <h1 id="hero-heading">Check your rota against the working time rules</h1>
        <p class="lead">
          Upload a rota from your rota software or your own spreadsheet. ShiftLens checks rest,
          breaks, hours and the extra rules for under-18s, and explains anything worth a second
          look.
        </p>
        <ul class="selling-points">
          <li>Free, with no sign-up and no email needed.</li>
          <li>Your rota never leaves your browser.</li>
          <li>Plain English, with a link to the rule behind every finding.</li>
        </ul>
        <p>
          <a class="button" href="#check">
            Check your rota
          </a>
        </p>
      </div>
      <RotaSketch />
    </section>
  );
}

export function HowItWorks() {
  return (
    <section aria-labelledby="how-heading">
      <h2 id="how-heading">How it works</h2>
      <ol class="steps">
        <li>
          <strong>Export your rota</strong> as a CSV or Excel file, from your rota software or the
          spreadsheet you already use.
        </li>
        <li>
          <strong>Upload it here</strong> and check we've read your columns correctly.
        </li>
        <li>
          <strong>Get your report straight away:</strong> a summary, then person by person and shift
          by shift, with a week view and how you could fix each thing we find.
        </li>
      </ol>
    </section>
  );
}

export function WhatItChecks() {
  return (
    <section aria-labelledby="checks-heading">
      <h2 id="checks-heading">What ShiftLens checks</h2>
      <ul class="checks">
        <Check source={GOV.restBreaks} under18={GOV.youngWorkers}>
          <strong>Rest between working days:</strong> 11 hours for adults, 12 hours for under-18s.
        </Check>
        <Check source={GOV.restBreaks} under18={GOV.youngWorkers}>
          <strong>Weekly rest:</strong> 24 hours off each week for adults (or 48 hours a fortnight),
          and 48 hours a week for under-18s.
        </Check>
        <Check source={GOV.restBreaks} under18={GOV.youngWorkers}>
          <strong>Breaks:</strong> 20 minutes for adults working more than 6 hours a day, and 30
          minutes for under-18s working more than 4.5 hours.
        </Check>
        <Check source={GOV.maxHours}>
          <strong>Under-18s' hours:</strong> no more than 8 hours a day or 40 hours a week.
        </Check>
        <Check source={GOV.nightWork}>
          <strong>Under-18s at night:</strong> no work between midnight and 4am, and usually not
          between 10pm and 6am.
        </Check>
        <Check source={GOV.maxHours}>
          <strong>The 48-hour week:</strong> an early warning if someone's hours look likely to
          average more than 48 a week, unless they've opted out.
        </Check>
        <Check source={GOV.nightWork}>
          <strong>Night workers:</strong> an early warning if their hours look likely to average
          more than 8 in every 24.
        </Check>
      </ul>
      <p>
        <a href="/explainer">Read the rules in plain English</a>, including the changes expected for
        zero-hours workers.
      </p>
    </section>
  );
}

function Check({
  source,
  under18,
  children,
}: {
  source: string;
  /** Extra link for under-18 figures that only appear on the young workers page. */
  under18?: string;
  children: ComponentChildren;
}) {
  return (
    <li>
      <p>{children}</p>
      <p class="sources">
        Source:{' '}
        <a href={source} target="_blank" rel="noopener noreferrer">
          gov.uk guidance
        </a>
        {under18 && (
          <>
            {' and '}
            <a href={under18} target="_blank" rel="noopener noreferrer">
              under-18s
            </a>
          </>
        )}
      </p>
    </li>
  );
}

export function Privacy() {
  return (
    <section aria-labelledby="privacy-heading">
      <h2 id="privacy-heading">Your rota never leaves your browser</h2>
      <p>
        ShiftLens reads your file and runs every check on your own computer or phone. Your staff
        names, shifts and dates aren't uploaded to us or anyone else, and they're gone when you
        close the page.
      </p>
      <p>
        There's no sign-up, no cookies, and we don't ask for your email. We count visits anonymously
        on our own server, to see how people find ShiftLens, and nothing from your rota is ever part
        of that. <a href="/privacy">Read our privacy notice</a>.
      </p>
    </section>
  );
}

export function Faq() {
  return (
    <section aria-labelledby="faq-heading">
      <h2 id="faq-heading">Questions</h2>
      <details>
        <summary>Is it really free?</summary>
        <p>
          Yes. The summary and the full report are both free, with no sign-up and no email address
          needed.
        </p>
      </details>
      <details>
        <summary>Do you see my staff's details?</summary>
        <p>
          No. Your rota is checked in your browser and never sent to us. We can't see your staff's
          names, shifts or anything else in your file.
        </p>
      </details>
      <details>
        <summary>What files can I use?</summary>
        <p>
          A CSV or Excel (.xlsx) file up to 5 MB. Each row should be one shift, with a name or staff
          ID, a date, and start and end times. A column for breaks, and dates of birth for anyone
          under 18, help us check more.
        </p>
      </details>
      <details>
        <summary>Can it tell me for certain whether my rota is legal?</summary>
        <p>
          No. A rota shows planned shifts. It can't show contracts, workforce agreements, opt-outs,
          or the hours people actually worked, and some rules have exceptions. Treat what ShiftLens
          finds as things to check, not final answers.
        </p>
      </details>
      <details>
        <summary>Does it cover Northern Ireland?</summary>
        <p>
          No. Northern Ireland has its own working time rules. ShiftLens covers England, Scotland
          and Wales.
        </p>
      </details>
      <details>
        <summary>What about the new rights for zero-hours workers?</summary>
        <p>
          New rights for zero-hours and low-hours workers, including notice of shifts and payment
          for shifts cancelled at short notice, are expected but aren't in force yet. Our{' '}
          <a href="/explainer#whats-next">explainer</a> sets out what's confirmed so far.
        </p>
      </details>
    </section>
  );
}
